<template>
  <div class="integral-setting-page">
    <div class="zhanwei4"></div>

    <!-- 顶部统一操作栏：标题 + 保存按钮 -->
    <div class="setting-header-card">
      <div class="card-left">
        <div class="icon-avatar">
          <el-icon><Medal /></el-icon>
        </div>
        <div class="text-group">
          <h2 class="title">积分管理</h2>
          <span class="sub-text"
            >根据游戏难度自动计算基础分，配置过关奖励及答错/求助惩处机制</span
          >
        </div>
      </div>

      <div class="card-right">
        <el-button
          type="primary"
          class="top-save-btn"
          :loading="saving"
          @click="saveSettings"
        >
          <el-icon><Check /></el-icon>
          保存
        </el-button>
      </div>
    </div>

    <!-- 主表单卡片 -->
    <el-form
      :model="form"
      label-width="auto"
      label-position="top"
      class="integral-main-form"
    >
      <!-- 分组一：基础分与时间规则 -->
      <div class="form-section-block">
        <div class="section-title-row">
          <span class="indicator-bar"></span>
          <span class="section-name">时间与基础得分机制</span>
          <span class="section-hint">基础分随难度锁定联动（不可手动更改）</span>
        </div>

        <el-row :gutter="24">
          <el-col :xs="24" :sm="12">
            <el-form-item label="游戏标准时长">
              <el-input
                type="number"
                v-model="form.gameTime"
                disabled
                placeholder="0"
              >
                <template #append>分钟</template>
              </el-input>
            </el-form-item>
          </el-col>

          <el-col :xs="24" :sm="12">
            <el-form-item label="基础分（根据游戏难度自动设定）">
              <el-input
                type="number"
                v-model="form.fullMarks"
                disabled
                placeholder="根据难度联动"
              >
                <template #append>分</template>
              </el-input>
            </el-form-item>
          </el-col>

          <el-col :xs="24" :sm="12">
            <el-form-item label="多长时间内过关获得基础分（分钟）">
              <el-input
                type="number"
                min="0"
                clearable
                v-model.number="form.parlayTime"
                placeholder="建议根据关卡总数折算"
              >
                <template #append>分钟内</template>
              </el-input>
            </el-form-item>
          </el-col>

          <el-col :xs="24" :sm="12">
            <el-form-item label="每过一关积分奖励">
              <el-input
                type="number"
                min="0"
                clearable
                v-model.number="form.levelPoints"
                placeholder="建议设为 100 分每关"
                @input="handleLevelPointsChange"
              >
                <template #append>分/关</template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>
      </div>

      <!-- 分组二：扣分惩罚机制 -->
      <div class="form-section-block">
        <div class="section-title-row">
          <span class="indicator-bar warning"></span>
          <span class="section-name">惩处与消耗扣分机制</span>
          <span class="section-hint">试错、求助与跳过扣分机制配置</span>
        </div>

        <el-row :gutter="24">
          <el-col :xs="24" :sm="12">
            <el-form-item label="超时扣分">
              <el-input
                type="number"
                min="0"
                clearable
                v-model.number="form.timeoutDeducted"
                placeholder="0"
              >
                <template #append>分</template>
              </el-input>
            </el-form-item>
          </el-col>

          <el-col :xs="24" :sm="12">
            <el-form-item label="每次答错扣分">
              <el-input
                type="number"
                min="0"
                clearable
                v-model.number="form.incorrectAnswer"
                placeholder="建议设为 10 到 20 分"
              >
                <template #append>分/次</template>
              </el-input>
            </el-form-item>
          </el-col>

          <el-col :xs="24" :sm="12">
            <el-form-item label="使用提示扣分">
              <el-input
                type="number"
                min="0"
                clearable
                v-model.number="form.promptsDeduct"
                placeholder="建议设为 50 分"
              >
                <template #append>分/次</template>
              </el-input>
            </el-form-item>
          </el-col>

          <el-col :xs="24" :sm="12">
            <el-form-item label="跳过关卡扣分（自动关联过关积分奖励）">
              <el-input
                type="number"
                v-model="form.skippingParlay"
                disabled
                placeholder="与每过一关积分奖励一致"
              >
                <template #append>分/关</template>
              </el-input>
            </el-form-item>
          </el-col>

          <el-col :xs="24" :sm="12">
            <el-form-item label="使用 GPS 定位帮助扣分">
              <el-input
                type="number"
                min="0"
                clearable
                v-model.number="form.sosPoints"
                placeholder="建议设为 50 分"
              >
                <template #append>分/次</template>
              </el-input>
            </el-form-item>
          </el-col>

          <el-col :xs="24" :sm="12">
            <el-form-item label="关卡数量">
              <el-input
                type="number"
                v-model="form.numberLevel"
                disabled
                placeholder="0"
              >
                <template #append>关</template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>
      </div>

      <!-- 游戏最高得分结算规则（加粗放大，去除原保存按钮） -->
      <div class="score-calculation-card">
        <div class="calc-header">
          <div class="calc-header-left">
            <span class="calc-title-prominent">游戏最高得分</span>
            <span class="calc-formula-desc"
              >计算公式：基础分 + 关卡数 × 每过一关积分奖励</span
            >
          </div>
          <el-tag type="info" effect="plain" class="rule-tag">理论上限</el-tag>
        </div>

        <div class="calc-display-box">
          <div class="formula-process">
            <div class="term-box">
              <span class="term-label">基础分</span>
              <span class="num-badge">{{ form.fullMarks || 0 }}</span>
            </div>
            <span class="operator">+</span>
            <div class="term-box">
              <span class="term-label">关卡数</span>
              <span class="num-badge">{{ form.numberLevel || 0 }}</span>
            </div>
            <span class="operator">×</span>
            <div class="term-box">
              <span class="term-label">过关奖励</span>
              <span class="num-badge">{{ form.levelPoints || 0 }}</span>
            </div>
            <span class="operator">=</span>
          </div>

          <div class="total-score-value">
            <span class="label">最高得分封顶</span>
            <span class="score-number">{{ totalPoint || 0 }}</span>
            <span class="unit">分</span>
          </div>
        </div>
      </div>
    </el-form>

    <div class="zhanwei3"></div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { ElMessage } from "element-plus";
import { Medal, Check } from "@element-plus/icons-vue";

export default defineComponent({
  name: "IntegralSetting",
  components: { Medal, Check },
  data() {
    return {
      gameId: "",
      saving: false,
      form: {
        id: "",
        gameTime: 0,
        timeoutDeducted: 0,
        parlayTime: 0,
        fullMarks: 500, // 基础分
        levelPoints: 100, // 每过一关积分奖励
        incorrectAnswer: 10, // 每次答错扣分
        numberLevel: 0,
        promptsDeduct: 50, // 使用提示扣分
        skippingParlay: 100, // 跳过关卡扣分（联动关卡积分）
        sosPoints: 50, // 使用GPS定位帮助扣分
      },
    };
  },
  computed: {
    totalPoint(): number {
      const fullMarks = Number(this.form.fullMarks) || 0;
      const numberLevel = Number(this.form.numberLevel) || 0;
      const levelPoints = Number(this.form.levelPoints) || 0;
      return fullMarks + numberLevel * levelPoints;
    },
  },
  mounted() {
    const { id } = this.$route.params;
    this.gameId = id as string;
    if (this.gameId) {
      this.initRelease(this.gameId);
    }
  },
  methods: {
    // 监听关卡积分改动，自动联动跳过关卡扣分
    handleLevelPointsChange(val: any) {
      this.form.skippingParlay = Number(val) || 0;
    },
    // 根据难度等级换算基础分 (1星=500, 2星=750, 3星=1000, 4星=1250, 5星=1500)
    calcBaseScoreByDifficulty(difficulty: number | string): number {
      const diffMap: Record<number, number> = {
        1: 500,
        2: 750,
        3: 1000,
        4: 1250,
        5: 1500,
      };
      const key = Number(difficulty) || 1;
      return diffMap[key] || 500;
    },
    initRelease(id: string) {
      this.$store
        .dispatch("game/getBonus", { gameId: id })
        .then((res: any) => {
          if (res) {
            this.form = { ...this.form, ...res };

            // 联动 1：跳过关卡扣分跟随过关积分奖励
            if (this.form.levelPoints) {
              this.form.skippingParlay = this.form.levelPoints;
            }

            // 联动 2：尝试读取当前游戏的难度设定并锁定基础分
            const currentGame = this.$store.state.game.games?.find(
              (g: any) => g.id == id
            );
            if (currentGame && currentGame.difficulty) {
              this.form.fullMarks = this.calcBaseScoreByDifficulty(
                currentGame.difficulty
              );
            }
          }
        })
        .catch((err: any) => {
          console.error(err);
        });
    },
    saveSettings() {
      this.saving = true;
      // 提交前确保联动值精准一致
      this.form.skippingParlay = this.form.levelPoints;

      this.$store
        .dispatch("game/updateBonus", { integralBean: this.form })
        .then(() => {
          ElMessage.success("积分规则已保存");
        })
        .catch((err: any) => {
          ElMessage.error("保存失败: " + (err.message || err));
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
});
</script>

<style lang="less" scoped>
@primary-color: #5d5fef;
@primary-light: #f0f2ff;
@text-main: #1e293b;
@text-sub: #64748b;
@text-muted: #94a3b8;
@border-color: #e2e8f0;
@card-border: #f1f5f9;
@warning-color: #f59e0b;

.zhanwei4 {
  height: 20px;
}

.zhanwei3 {
  height: 32px;
}

.integral-setting-page {
  width: 100%;
  max-width: 1080px;
  margin: 0 auto;
  padding: 0 20px;
  box-sizing: border-box;
}

// 顶部统一操作栏
.setting-header-card {
  background: #ffffff;
  border: 1px solid @card-border;
  border-radius: 14px;
  padding: 18px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.03);
  margin-bottom: 24px;

  .card-left {
    display: flex;
    align-items: center;
    gap: 14px;

    .icon-avatar {
      width: 42px;
      height: 42px;
      border-radius: 10px;
      background: @primary-light;
      color: @primary-color;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 22px;
      flex-shrink: 0;
    }

    .text-group {
      .title {
        margin: 0 0 4px 0;
        font-size: 17px;
        font-weight: 700;
        color: @text-main;
      }
      .sub-text {
        font-size: 12.5px;
        color: @text-muted;
      }
    }
  }

  .card-right {
    .top-save-btn {
      height: 40px;
      padding: 0 24px;
      border-radius: 8px;
      font-weight: 600;
      background-color: @primary-color;
      border-color: @primary-color;
      box-shadow: 0 4px 12px rgba(93, 95, 239, 0.25);
      transition: all 0.2s ease;

      .el-icon {
        margin-right: 6px;
      }

      &:hover {
        background-color: #4b4de0;
        border-color: #4b4de0;
        transform: translateY(-1px);
        box-shadow: 0 6px 16px rgba(93, 95, 239, 0.35);
      }
    }
  }
}

// 主表单卡片
.integral-main-form {
  background: #ffffff;
  border: 1px solid @card-border;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.04);
  box-sizing: border-box;

  .form-section-block {
    margin-bottom: 30px;

    .section-title-row {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 20px;
      padding-bottom: 10px;
      border-bottom: 1px solid @border-color;

      .indicator-bar {
        width: 4px;
        height: 16px;
        background: @primary-color;
        border-radius: 2px;

        &.warning {
          background: @warning-color;
        }
      }

      .section-name {
        font-size: 15px;
        font-weight: 700;
        color: @text-main;
      }

      .section-hint {
        font-size: 12px;
        color: @text-muted;
      }
    }

    :deep(.el-form-item) {
      margin-bottom: 22px;

      .el-form-item__label {
        font-size: 13px;
        font-weight: 600;
        color: @text-main;
        padding-bottom: 6px;
      }
    }

    :deep(.el-input__wrapper) {
      border-radius: 8px 0 0 8px;
      box-shadow: 0 0 0 1px @border-color inset;
      transition: all 0.2s ease;

      &:hover {
        box-shadow: 0 0 0 1px #cbd5e1 inset;
      }

      &.is-focus {
        box-shadow: 0 0 0 1px @primary-color inset !important;
      }

      &.is-disabled {
        background-color: #f8fafc;
        box-shadow: 0 0 0 1px @border-color inset;

        .el-input__inner {
          color: #475569;
          -webkit-text-fill-color: #475569;
          font-weight: 600;
        }
      }
    }

    :deep(.el-input-group__append) {
      background-color: #f8fafc;
      border-radius: 0 8px 8px 0;
      box-shadow: 0 0 0 1px @border-color inset;
      color: @text-sub;
      font-size: 12px;
      font-weight: 500;
      padding: 0 14px;
    }
  }
}

// 结算公式看板卡片
.score-calculation-card {
  margin-top: 10px;
  padding: 24px;
  background: #f8fafc;
  border-radius: 14px;
  border: 1px solid @border-color;
  box-sizing: border-box;

  .calc-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 18px;

    .calc-header-left {
      display: flex;
      align-items: baseline;
      gap: 12px;

      // 强化加粗放大
      .calc-title-prominent {
        font-size: 18px;
        font-weight: 800;
        color: @text-main;
        letter-spacing: 0.3px;
      }

      .calc-formula-desc {
        font-size: 12.5px;
        color: @text-muted;
      }
    }

    .rule-tag {
      border-radius: 6px;
    }
  }

  .calc-display-box {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #ffffff;
    border-radius: 12px;
    padding: 18px 24px;
    border: 1px solid @border-color;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.03);

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 16px;
      align-items: flex-start;
    }

    .formula-process {
      display: flex;
      align-items: center;
      gap: 12px;

      .term-box {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;

        .term-label {
          font-size: 11px;
          color: @text-muted;
        }

        .num-badge {
          background: #f1f5f9;
          color: @text-main;
          padding: 5px 12px;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
          font-weight: 700;
          font-size: 15px;
          min-width: 32px;
          text-align: center;
        }
      }

      .operator {
        color: @text-muted;
        font-weight: bold;
        font-size: 16px;
        margin-top: 18px;
      }
    }

    .total-score-value {
      display: flex;
      align-items: baseline;
      gap: 6px;

      .label {
        font-size: 13px;
        color: @text-sub;
        font-weight: 600;
      }

      .score-number {
        font-size: 32px;
        font-weight: 900;
        color: @primary-color;
        line-height: 1;
        letter-spacing: -0.5px;
      }

      .unit {
        font-size: 13px;
        color: @primary-color;
        font-weight: 700;
      }
    }
  }
}
</style>
